import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { useRef, useState } from 'react';

import { Link } from 'react-router';
import Slider from 'react-slick';

import { useAppDispatch } from 'dux/app/hooks';

import { Box, Text, theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';
import { mediaMinWidth } from '@prose-ui/utils/media';

import { CarouselDots } from 'Apps/Products/Blocks/CarouselDots';

import { SliderArrow } from 'Components/SliderArrow';

import { formatPriceWithCurrency } from 'utils/currencies';
import useResponsiveVariant from 'utils/useResponsiveVariant';

import * as trackingActions from 'dux/tracking/actions';
import { trackHeapEvent } from 'dux/tracking/actions';

import BlockLayout from './BlockLayout';

export type ProductCardType = {
  currency: string;
  slug: string;
  productPageUrl: string;
  bgLabelledPicture: { src: string };
  bgPicture: { src: string };
  label: string;
  regularPrice: number;
};

type ProductsCarouselProps = {
  background?: string;
  style?: React.CSSProperties;
  content: {
    cards: ProductCardType[];
    description?: string;
    headline: string;
  };
  dataFrom?: string;
  displayDescription?: boolean;
  margins?: string | null;
};

const StyledSlider = styled(Slider)`
  width: 100%;

  ${mediaMinWidth('md')} {
    padding: 0 190px;
  }
`;

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 450px;
  padding-left: 32px;

  color: inherit;

  ${mediaMinWidth('sm')} {
    padding: 0 8px;
  }
`;

const StyledImage = styled.img`
  width: 100%;
  height: 356px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;

  object-fit: cover;
`;

const CardContent = styled.div`
  max-height: 94px;
  padding: ${theme.spacing['6x']};
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;

  background-color: #f1e6db;
`;

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
`;

export const ProductsCarousel = ({
  background = theme.colors.neutral[200],
  style,
  content,
  dataFrom = 'products-block',
  margins,
}: ProductsCarouselProps) => {
  const sliderRef = useRef<Slider>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setHovered] = useState<number | null>(null);
  const [displayArrows, setDisplayArrows] = useState(false);
  const dispatch = useAppDispatch();
  const { isMobile } = useResponsiveVariant();

  const onAfterChange = (slide: number) => {
    setCurrentSlide(Math.round(slide));
    dispatch(trackHeapEvent('Product Upsell Module Carousel Scroll'));
  };

  return (
    // @ts-expect-error BlockLayout no migrated
    <BlockLayout
      backgroundColor={background}
      blockLayoutMargin={margins || (isMobile ? '48px auto' : '144px auto')}
      contentAlign="center"
      contentWidth="veryWide"
      style={style}
    >
      <Text color="primary.300" marginBottom="10x" variant="h3">
        {content.headline}
      </Text>
      <SliderContainer
        onMouseEnter={() => setDisplayArrows(true)}
        onMouseLeave={() => setDisplayArrows(false)}
      >
        {!isMobile && displayArrows && (
          <SliderArrow
            dataFrom="ingredients-module-carousel"
            direction="prev"
            leftAndRight={-16}
            onClick={() => sliderRef?.current?.slickPrev()}
            top={45}
          />
        )}
        <StyledSlider
          ref={sliderRef}
          afterChange={onAfterChange}
          arrows={false}
          infinite
          slidesToScroll={1}
          slidesToShow={isMobile ? 1.1 : content.cards.length >= 3 ? 3 : content.cards.length}
        >
          {content.cards.map(
            (
              { currency, slug, productPageUrl, bgLabelledPicture, bgPicture, label, regularPrice },
              i,
            ) => (
              <Card
                key={label}
                data-click={`see-product-${slug}`}
                data-from={dataFrom}
                data-testid={`product-item-card-${slug}`}
                onMouseEnter={() => {
                  setHovered(i);
                  dispatch(
                    trackingActions.trackHover({
                      dataFrom,
                      dataClick: 'see-product',
                      productSlug: slug,
                    }),
                  );
                }}
                onMouseLeave={() => setHovered(null)}
                to={productPageUrl}
              >
                <StyledImage
                  alt={label}
                  src={(isHovered === i ? bgLabelledPicture?.src : bgPicture?.src) ?? ''}
                />
                <CardContent>
                  <Text variant="body3medium">{label}</Text>
                  <Text variant="body3regular">
                    {formatPriceWithCurrency({ price: regularPrice, currency })}
                  </Text>
                </CardContent>
              </Card>
            ),
          )}
        </StyledSlider>
        {!isMobile && displayArrows && (
          <SliderArrow
            dataFrom="ingredients-module-carousel"
            direction="next"
            leftAndRight={-16}
            onClick={() => sliderRef?.current?.slickNext()}
            top={45}
          />
        )}
        <Box marginTop="10x">
          <CarouselDots
            currentIndex={Math.round(currentSlide)}
            dotsQuantity={content.cards.length}
            onClick={(dotIndex: number) => {
              sliderRef.current?.slickGoTo(dotIndex, false);
              setCurrentSlide(dotIndex);
            }}
          />
        </Box>
      </SliderContainer>
    </BlockLayout>
  );
};
